import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { setCookie } from 'cookies-next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

import Hidden from '@/components/ui/TzoHidden';
import type { LayoutWrapperContents } from '@/types/LayoutWrapperContents';

// ✅ Lazy load heavy components
const Header = dynamic(() => import('@/components/nav/Header'));
const Footer = dynamic(() => import('@/components/nav/Footer'));
const Sidebar = dynamic(() => import('@/components/nav/Sidebar'), {
  ssr: false,
});
const RightSidebar = dynamic(() => import('@/components/nav/RightSidebar'), {
  ssr: false,
});
const ProgressBar = dynamic(() => import('@/components/ui/ProgressBar'), {
  ssr: false,
});
const ScrollToTop = dynamic(() => import('react-scroll-to-top'), {
  ssr: false,
});

interface Props {
  children: React.ReactNode;
  LayoutWrapperContents?: LayoutWrapperContents;
}

const LayoutWrapper = ({ children, LayoutWrapperContents }: Props) => {
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'), {
    defaultMatches: true,
  });

  const [openSidebar, setOpenSidebar] = useState(false);
  const [openRightSidebar, setOpenRightSidebar] = useState(false);
  const [isAppRequest, setIsAppRequest] = useState(false);
  const router = useRouter();
  const [closeSticky, setCloseSticky] = useState(false);
  const [layoutWrapperContents, setLayoutWrapperContents] =
    useState<LayoutWrapperContents>();

  const defaultLayoutWrapperContents: LayoutWrapperContents = {
    Heading: 'Missed e-filing Form 2290 for the 2023 tax year?',
    SubHeading: 'E-file now at just $19.99 and reduce IRS penalties',
    CtaText: 'E-file Form 2290 Now',
    CtaSubHeading: `Schedule 1`,
    CtaURL: `${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form2290sticky`,
    isActive: false,
  };

  const handleSidebarOpen = (): void => setOpenSidebar(true);
  const handleSidebarClose = (): void => setOpenSidebar(false);
  const handleRightSidebarOpen = (): void => setOpenRightSidebar(true);
  const handleRightSidebarClose = (): void => setOpenRightSidebar(false);
  const handleStickyClose = (): void => setCloseSticky(true);

  const open = isLg ? false : openSidebar;
  const openRight = isLg ? false : openRightSidebar;

  useEffect(() => {
    const pathName = router.pathname;
    const source = router.query?.source ?? '';
    if (
      source === 'app' &&
      (pathName === '/terms-of-use' || pathName === '/privacy-policy')
    ) {
      setIsAppRequest(true);
    }
    const refcode = router.query?.tzorefcode ?? '';
    if (refcode !== undefined && refcode !== '') {
      setCookie('tzo_refcode', refcode, {
        domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
        path: '/',
        secure: true,
        sameSite: 'lax',
      } as any);
    }
  }, [router.isReady]);

  useEffect(() => {
    if (LayoutWrapperContents !== undefined) {
      setLayoutWrapperContents(LayoutWrapperContents);
    } else {
      setLayoutWrapperContents(defaultLayoutWrapperContents);
    }
  }, [LayoutWrapperContents]);

  return (
    <Box
      id="page-top"
      sx={{
        backgroundColor: theme.palette.background.default,
        height: '100%',
        backgroundSize: {
          lg: 'auto',
          md: 'auto',
          sm: 'contain',
          xs: 'contain',
        },
      }}
    >
      <ProgressBar />
      {!isAppRequest && (
        <>
          <Header
            onSidebarOpen={handleSidebarOpen}
            onRightSidebarOpen={handleRightSidebarOpen}
          />
          <Sidebar onClose={handleSidebarClose} open={open} />
          <RightSidebar onClose={handleRightSidebarClose} open={openRight} />
        </>
      )}
      <Box
        component="main"
        sx={{ my: 0, mx: 'auto', height: '100%' }}
        className="main-content"
      >
        <Box component="article">{children}</Box>
      </Box>
      {!isAppRequest && <Footer />}
      {!closeSticky && layoutWrapperContents?.isActive === true && (
        <Hidden smDown>
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              width: '100%',
              left: 0,
              right: 0,
              zIndex: 3,
            }}
          >
            <Container
              maxWidth="md"
              sx={{
                backgroundColor: '#fff',
                mx: { xs: 3, md: 'auto' },
                width: 'auto',
                py: 1.5,
                borderTopLeftRadius: 6,
                borderTopRightRadius: 6,
                boxShadow: '0 0 20px rgba(0,0,0,.5)',
                position: 'relative',
                borderWidth: 2,
                borderColor: '#414042',
                borderStyle: 'solid',
                borderBottom: 'none',
              }}
            >
              <Tooltip title="Close" arrow placement="top">
                <IconButton
                  aria-label="close"
                  size="small"
                  onClick={handleStickyClose}
                  sx={{
                    position: 'absolute',
                    right: -12,
                    top: -12,
                    color: '#fff',
                    backgroundColor: '#e11d1d',
                    '&:hover, &:focus': {
                      backgroundColor: '#b01919',
                    },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1, sm: 2, md: 4 }}
                sx={{ justifyContent: 'space-between', alignItems: 'center' }}
              >
                <Box>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: { xs: 14, sm: 16, md: 20 },
                      fontWeight: 600,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: layoutWrapperContents?.Heading,
                    }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: { xs: 11, sm: 13, md: 16 },
                      fontWeight: 700,
                      color: '#213e99',
                    }}
                    dangerouslySetInnerHTML={{
                      __html: layoutWrapperContents?.SubHeading,
                    }}
                  />
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Button
                    variant="contained"
                    size="medium"
                    sx={{
                      textTransform: 'none',
                      px: 2.5,
                      display: 'inline-block',
                      boxShadow: 'none',
                      borderRadius: 4.2,
                      backgroundColor: '#213e99',
                      whiteSpace: 'nowrap',
                      '&:hover, &:focus': {
                        backgroundColor: '#172b6b',
                      },
                    }}
                    href={layoutWrapperContents?.CtaURL!}
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: layoutWrapperContents?.CtaText,
                      }}
                    />
                  </Button>
                  <Typography
                    sx={{
                      fontSize: 11,
                      textAlign: 'center',
                      mt: 0.5,
                      mb: -1,
                      whiteSpace: 'nowrap',
                    }}
                    dangerouslySetInnerHTML={{
                      __html: layoutWrapperContents?.CtaSubHeading,
                    }}
                  />
                </Box>
              </Stack>
            </Container>
          </Box>
        </Hidden>
      )}

      <Tooltip title="Back to top" arrow placement="right" describeChild>
        <Box
          sx={{
            position: 'fixed',
            zIndex: 9,
            right: 35,
            bottom:
              process.env.NEXT_PUBLIC_ENVIRONMENT === 'production' ? 165 : 40,
          }}
        >
          <ScrollToTop
            smooth
            color="#213e99"
            className="scroll-top"
            viewBox="0 0 24 24"
            svgPath="M8.12 14.71 12 10.83l3.88 3.88c.39.39 1.02.39 1.41 0s.39-1.02 0-1.41L12.7 8.71a.996.996 0 0 0-1.41 0L6.7 13.3c-.39.39-.39 1.02 0 1.41.39.38 1.03.39 1.42 0"
          />
        </Box>
      </Tooltip>
    </Box>
  );
};

export default LayoutWrapper;
